import React, { useContext, useEffect, useState, useRef } from 'react';
import classNames from 'classnames';

import {
  extend, params, string, useDataModel, number, customType,
  shape, bool, arrayOf, QueryContext, QueryProvider
} from '@thd-nucleus/data-sources';
import { PIPRedirector, DynamicComponent } from '@thd-nucleus/app-render';
import { ExperienceContext, useStoreId, useConfigService } from '@thd-nucleus/experience-context';
import { CustomerContext, useThdCustomer } from '@thd-olt-functional/customer-information';

import { AisleBay } from '@thd-olt-component-react/aisle-bay';
import { Breadcrumbs } from '@thd-olt-component-react/breadcrumbs';
import {
  Col, Row, Placeholder
} from '@thd-olt-component-react/core-ui';
import { Avataar, RoomVoInspire, RoomVo } from '@thd-olt-component-react/augmented-reality';
import { CompatibleAccessories } from '@thd-olt-component-react/compatible-accessories';
import { ConsumerCardMessaging } from '@thd-olt-component-react/consumer-card-messaging';
import { DeliveryAndInstallation } from '@thd-olt-component-react/delivery-and-installation';
import { Discontinued } from '@thd-olt-component-react/discontinued';
import { ErrorBoundary } from '@thd-olt-component-react/error-boundary';
import { FeatureBasedRecommendationsImporter } from '@thd-olt-component-react/feature-based-recommendations';
import { Hydrator, declareContexts } from '@thd-olt-component-react/hydrator';
import { MediaGalleryMobile, MediaGalleryCarousel } from '@thd-olt-component-react/media-gallery';
import { Metadata, ProductMetadata } from '@thd-olt-component-react/metadata';
import { Promotion } from '@thd-olt-component-react/promotion';
import { Price, PriceClearanceDataModel } from '@thd-olt-component-react/price';
import { ProductDetails } from '@thd-olt-component-react/product-details';
import { ProductInfoBar } from '@thd-olt-component-react/product-info-bar';
import { PromotionProductsAnchor } from '@thd-olt-component-react/promotion-products';
import { ProductConfigurator } from '@thd-olt-component-react/product-configurator';
import { ProductOverviewAccordion } from '@thd-olt-component-react/product-overview';
import { QuestionsAndAnswersAccordion } from '@thd-olt-component-react/questions-and-answers';
import { RatingsAndReviewsAccordion } from '@thd-olt-component-react/ratings-and-reviews';
import { RelatedSearchImporter, PopularVariationsImporter } from '@thd-olt-component-react/related-search';
import { useNavigate, useLocation, useParams } from '@thd-olt-component-react/router';
import { SalientPoints } from '@thd-olt-component-react/salient-points';
import { SpecificationsAccordion } from '@thd-olt-component-react/specifications';
import { SponsoredPipBanner } from '@thd-olt-component-react/sponsored-content';
import { BazaarVoiceCarousel } from '@thd-olt-component-react/social-media-container';
import { RetailMediaCarousel } from '@thd-olt-component-react/retail-media-pla-sponsored-placements';
import { StickyNav } from '@thd-olt-component-react/sticky-nav';
import { SuperSku } from '@thd-olt-component-react/super-sku';
import { TextAnExpert } from '@thd-olt-component-react/text-an-expert';
import {
  DynamicRecsWrapper,
  PipSemWrapper,
  RecentlyViewedWrapper
} from '@thd-olt-component-react/thd-recs-containers';
import { Sharebelt, ShareFavorite } from '@thd-olt-component-react/thd-sharebelt';
import { WhatWeOffer } from '@thd-olt-component-react/what-we-offer';
import { FitCompatibility } from '@thd-olt-component-react/fit-compatibility';
import { useTheme } from '@thd-olt-component-react/theme-provider';
import { ProductPageEventCountdownTimer } from '@thd-olt-component-react/event-countdown-timer';
import { BundlePmvFbtLayoutMa } from '@thd-olt-component-react/bundle-mini';
import { PackagesMiniABTest } from '../../components/PackagesMiniABTest';
import ZoneCard from '../../components/zone-card';
import {
  MajorApplianceBuyBoxLoad, MajorApplianceBuyBox, MajorApplianceBuyBoxModel,
  AugmentedReality, AugmentedRealityLoad,
  MagicApronBotPrompt, MagicApronBotPromptLoad
} from './dynamic-imports';
import { EmtLinksLoad, EmtLinks, EmtLinksModel } from '../shared-dynamic-imports';
import {
  shouldDisplayVisuallySimilar,
  shouldDisplayIrgaccessories,
  shouldDisplayIrgcollections,
  getProductTaxonomy
} from './product-utils';
import {
  updateStickyHeader,
  updateHeaderConfiguration,
} from '../utils';
import {
  HYDRATION_STANDARD_SCROLL_BUFFER,
  HYDRATION_STANDARD_DELAY_TIMER,
  RECS_REQUEST_KEY,
  STICKY_HEADER_HEIGHT,
  ONLINE_STORE_ID
} from '../../constants';
import { useAutoScrollToId } from '../../hooks/useAutoScrollToId';
import { useShouldDisplayReferrerRecommendations } from '../../hooks/useShouldDisplayReferrerRecommendations';
import '../../styles/product-page.scss';

export const ProductMobile = () => {
  declareContexts([
    QueryContext,
    ExperienceContext,
    CustomerContext,
  ]);

  const theme = useTheme(ProductMobile);
  const {
    hideConsumerCardMessaging, hideSponsoredCarousel, hideSponsoredPipBanner,
    hideAddToList, hideAisleBay, hideYouMightAlsoNeed,
    hideRelatedProducts
  } = theme.props;

  const thdCustomer = useThdCustomer();
  const ctx = useContext(ExperienceContext);
  const { defaultVariables } = useContext(QueryContext);
  const qctx = useContext(QueryContext);
  const path = ctx?.path || '';
  const arRef = useRef(null);

  const { itemId } = useParams();
  const storeId = useStoreId();
  let navigate = useNavigate();
  const { pathname } = useLocation();

  const fsFilmstrip = useConfigService('fs:isFilmstripEnable');
  const fsFilmstripUserImages = useConfigService('fs-prop:filmstrip-user-images-ab');
  const magicApronQABotEnabled = useConfigService('fs-prop:magic-apron-qa-bot-enabled');

  const productRatingsAndReviewsRef = useRef();

  const openAccordion = (accordion) => {
    window.LIFE_CYCLE_EVENT_BUS.trigger(`${accordion}.click`, false);
  };

  const { isConsumerApp, customer: { type: customerType } } = ctx;
  const isB2BCustomer = customerType === 'b2b';

  const { data: productData } = useDataModel('clientOnlyProduct', {
    variables: {
      itemId,
      storeId
    },
    skip: !itemId || storeId === ONLINE_STORE_ID || !storeId,
    ssr: false
  });

  useEffect(() => {
    updateStickyHeader('Header3-sticky');
    updateHeaderConfiguration('default')
  }, []);

  useAutoScrollToId(STICKY_HEADER_HEIGHT.MOBILE);

  const pipRecsPositionAbove = useConfigService('fs:pipRecsPositionAbove');
  const pipRecsPositionBelow = useConfigService('fs:pipRecsPositionBelow');
  const pipRecsPositionDefault = useConfigService('fs:pipRecsPositionDefault');
  const isPipRecsAbTest = pipRecsPositionAbove || pipRecsPositionBelow || pipRecsPositionDefault;
  const referer = useContext(ExperienceContext)?.referer;
  const shouldDisplayReferrerRecommendations = useShouldDisplayReferrerRecommendations(referer, path);
  const shouldDisplayReferrerRecsAbove = (shouldDisplayReferrerRecommendations && !isPipRecsAbTest)
    || pipRecsPositionAbove;
  const shouldDisplayReferrerRecsBelow = pipRecsPositionBelow;
  const shouldDisplayReferrerRecsDefault = (!shouldDisplayReferrerRecommendations && !isPipRecsAbTest)
    || pipRecsPositionDefault;

  const zoneAClasses = classNames('zone-a', 'overflow', {
    'zone-a--discontinued': !!productData?.product?.availabilityType?.discontinued,
  });

  const onSuperSkuChange = (data = {}) => {
    const { canonicalUrl } = data;
    if (canonicalUrl) {
      navigate(canonicalUrl, { state: { disableScroll: true } });
    }
  };

  const productTaxonomy = getProductTaxonomy(productData);

  const renderMediaGallery = () => {
    return (
      !fsFilmstrip
        ? (
          <MediaGalleryMobile
            itemId={itemId}
          />
        )
        : (
          <MediaGalleryCarousel
            itemId={itemId}
            overlayType="filmstrip"
            showLoader
            showUserImages={fsFilmstripUserImages}
            firstItemFetchPriority="high"
          />
        )
    )
  };

  return (
    <>
      <ErrorBoundary name="redirector">
        <PIPRedirector disablePEPRedirect disableCanonicalRedirect disableCMMMCRedirect />
      </ErrorBoundary>
      <ErrorBoundary name="product-metadata">
        <Metadata>
          <ProductMetadata itemId={itemId} />
        </Metadata>
      </ErrorBoundary>
      <ErrorBoundary name="pip-content">
        <Row className="isBound">
          <Row className="u--paddingTop u--paddingBottom">
            <Col sm="10" flatten>
              <ErrorBoundary name="breadcrumbs">
                <Breadcrumbs itemId={itemId} useStencilDesign />
              </ErrorBoundary>
            </Col>
          </Row>
          <Row>
            <Col sm="12" flatten>
              <ErrorBoundary name="eventbreadcrumbs">
                <Breadcrumbs itemId={itemId} shouldShowEventOnly useStencilDesign />
              </ErrorBoundary>
            </Col>
          </Row>
          {shouldDisplayReferrerRecsAbove && (
            <ZoneCard>
              <PipSemWrapper
                schemaName="pipsem"
                errorBoundary
                anchorId={itemId}
                hideATC
                withReferrer={shouldDisplayReferrerRecommendations}
              />
            </ZoneCard>
          )}
          <Row>
            <Col>
              <ErrorBoundary name="discontinued">
                <Discontinued itemId={itemId} />
              </ErrorBoundary>
            </Col>
            <Col sm="7" nopadding className="zone-card product--sticky">
              <div className="overflow u__clearfix">
                <Col sm="12">
                  <ErrorBoundary name="product-details">
                    <ProductDetails itemId={itemId}>
                      <ProductDetails.Badge itemId={itemId} />
                      <ProductDetails.BrandCollection showCollection showBrandHyperLink />
                      <div className="product-details__badge-title--wrapper">
                        <ProductDetails.Title />
                      </div>
                      {!hideAddToList && (
                        <div className="product-details__favorites sui-my-2">
                          <Sharebelt itemId={itemId}>
                            <ShareFavorite showCircle showFavoritesCount />
                          </Sharebelt>
                        </div>
                      )}
                      <a
                        className="product-details__ratings"
                        href="#product-section-rr"
                        onClick={() => openAccordion('product-section-rr')}
                        aria-label="Click on ratings"
                      >
                        <ProductDetails.Ratings
                          showBadge
                          target="#product-section-rr"
                          offsetselector="#sticky-nav"
                          qaselector="#product-section-qa"
                        />
                      </a>
                    </ProductDetails>
                  </ErrorBoundary>
                </Col>
                <Col sm="12">
                  <ErrorBoundary name="media-gallery">
                    {renderMediaGallery()}
                  </ErrorBoundary>
                </Col>
              </div>
            </Col>
            <Col sm="7" nopadding>
              <ErrorBoundary name="AvataarAR">
                <Col className="zone-card overflow ar-container">
                  <Avataar itemId={itemId} />
                </Col>
                <Col className="zone-card overflow">
                  <RoomVo itemId={itemId} />
                </Col>
              </ErrorBoundary>

              <ErrorBoundary name="augmented-reality">
                <DynamicComponent pageType="pip">
                  <Hydrator
                    id="product-section-ar"
                    delay={1500}
                    scrollBuffer={HYDRATION_STANDARD_SCROLL_BUFFER}
                    preserveCtxVal="clientStore"
                    waitFor={AugmentedRealityLoad}
                  >
                    <div className="zone-card overflow hide-empty">
                      <AugmentedReality forwardedRef={arRef} qctx={qctx} instructions itemId={itemId} />
                    </div>
                  </Hydrator>
                </DynamicComponent>
              </ErrorBoundary>
            </Col>
            <Col sm="5" flatten nopadding>
              <Row className={zoneAClasses}>
                <ZoneCard nopadding tag={Col}>
                  <ErrorBoundary name="event-countdown-timer">
                    <ProductPageEventCountdownTimer itemId={itemId} />
                  </ErrorBoundary>
                </ZoneCard>
                <ZoneCard tag={Col} className="u--paddingBottom">
                  <ErrorBoundary name="price">
                    <Price
                      channel="desktop"
                      displayEachUom={false}
                      itemId={itemId}
                      large
                      disableRangePricing
                      clsRemediation={{
                        placeholders: true,
                        preservePlaceholders: true
                      }}
                      showPreferredPricingBadge
                      showPreferredPricingBadgeToolTip
                    />
                  </ErrorBoundary>
                  <ErrorBoundary name="promotion">
                    <Promotion
                      hideConditionalPromoDescription
                      itemId={itemId}
                    />
                  </ErrorBoundary>
                  {!hideConsumerCardMessaging && (
                    <Col className="consumer-credit-messaging__card">
                      <ConsumerCardMessaging itemId={itemId} isB2B={isB2BCustomer} />
                    </Col>
                  )}
                </ZoneCard>
                <ZoneCard tag={Col} nopadding>
                  <ErrorBoundary name="promotion-products">
                    <PromotionProductsAnchor
                      itemId={itemId}
                      bmsmEnabled
                      isMultiSelect={(product) => !!product?.pricing?.conditionalPromotions?.[0]?.nvalues?.length}
                    />
                  </ErrorBoundary>
                </ZoneCard>
                {!hideAisleBay && (
                  <ZoneCard tag={Col}>
                    <ErrorBoundary name="aisle-bay">
                      <AisleBay
                        itemId={itemId}
                        clsRemediation={{
                          placeholders: true,
                          preservePlaceholders: false
                        }}
                      />
                    </ErrorBoundary>
                  </ZoneCard>
                )}
                <DynamicComponent pageType="pip">
                  <Hydrator
                    id="super-sku-and-buy-box"
                    className="grid"
                    placeholder={(
                      <Placeholder
                        color="#f5f5f5"
                        ready={false}
                        style={{ height: 500 }}
                        type="rect"
                      >
                        <div />
                      </Placeholder>
                    )}
                    delay={750}
                    scrollBuffer={100}
                    waitFor={MajorApplianceBuyBoxLoad}
                  >
                    <ZoneCard className="overflow" tag={Col}>
                      {productData?.product?.identifiers?.productType === 'CONFIGURABLE_BLINDS' && (
                        <ErrorBoundary name="product-configurator">
                          <ProductConfigurator itemId={itemId} />
                        </ErrorBoundary>
                      )}
                    </ZoneCard>
                    <ZoneCard tag={Col} className="overflow">
                      <ErrorBoundary name="super-sku">
                        <SuperSku
                          itemId={itemId}
                          onChange={onSuperSkuChange}
                          disableMediaAndAvailability
                        />
                      </ErrorBoundary>
                    </ZoneCard>
                    <ZoneCard tag={Col} nopadding>
                      <ErrorBoundary name="fit-compatibility">
                        <FitCompatibility
                          type="PIP"
                          itemId={itemId}
                          imageCacheEnabled
                        />
                      </ErrorBoundary>
                    </ZoneCard>
                    <Col flatten>
                      <MajorApplianceBuyBox
                        itemId={itemId}
                        sticky
                        qctx={qctx}
                        showCards
                        nopadding
                        showFavorites={isB2BCustomer}
                        showAddToProject={isB2BCustomer}
                      />
                    </Col>
                    <Col flatten>
                      <ErrorBoundary>
                        <CompatibleAccessories
                          itemId={itemId}
                          parent="PIP"
                        />
                      </ErrorBoundary>
                    </Col>
                  </Hydrator>
                </DynamicComponent>

              </Row>
            </Col>
          </Row>
          <ErrorBoundary name="product-info-bar">
            <ProductInfoBar itemId={itemId} compactMobile showOnMobile />
          </ErrorBoundary>
          {shouldDisplayReferrerRecsBelow && (
            <ZoneCard>
              <PipSemWrapper
                schemaName="pipsem"
                errorBoundary
                anchorId={itemId}
                hideATC
                withReferrer={shouldDisplayReferrerRecommendations}
              />
            </ZoneCard>
          )}
          <ErrorBoundary name="collapsible-sections">
            <DynamicComponent pageType="pip">
              <Hydrator
                id="collapsible-sections-accordion"
                className="grid"
                placeholder={(
                  <>
                    {[
                      'product-section-overview',
                      'product-section-key-feat',
                      'product-section-qa',
                      'product-section-rr'
                    ].map((id) => (
                      <div
                        id={id}
                        key={id}
                        className="accordion-placeholder"
                        ref={id === 'product-section-rr' ? productRatingsAndReviewsRef : null}
                      >
                        <Placeholder
                          width="100%"
                          height="82px"
                          color="#F5F5F5"
                          ready={false}
                          showLoadingAnimation
                          type="rect"
                        />
                      </div>
                    ))}
                  </>
                )}
              >
                <ErrorBoundary name="packages-mini-in-collapsible">
                  <BundlePmvFbtLayoutMa itemId={itemId} showPackagesMini />
                </ErrorBoundary>
                <div className="sui-flex sui-flex-col sui-w-full">
                  <ProductOverviewAccordion
                    itemId={itemId}
                    showReturnMessage
                    revJet
                  />
                  <SpecificationsAccordion
                    displaySimilarItems={false}
                    isWhiteSpecTitle
                    itemId={itemId}
                    oneColumn
                    withKeyProductFeatures
                  />
                  <QuestionsAndAnswersAccordion itemId={itemId} noSpacing />
                  <div
                    id="product-section-rr"
                    className="product-section-rr"
                    ref={productRatingsAndReviewsRef}
                  >
                    <RatingsAndReviewsAccordion
                      itemId={itemId}
                      noSpacing
                      prosAndCons
                    />
                  </div>
                </div>
              </Hydrator>
            </DynamicComponent>
          </ErrorBoundary>
          {magicApronQABotEnabled && !isB2BCustomer && (
            <ErrorBoundary name="magic-apron-qa-bot">
              <DynamicComponent pageType="pip">
                <Hydrator
                  id="product-section-magic-apron-qa-bot"
                  className="grid"
                  scrollBuffer={HYDRATION_STANDARD_SCROLL_BUFFER}
                  waitFor={MagicApronBotPromptLoad}
                  placeholder={<Placeholder width="100%" height="276px" color="#F5F5F5" />}
                >
                  <MagicApronBotPrompt qctx={qctx} itemId={itemId} />
                </Hydrator>
              </DynamicComponent>
            </ErrorBoundary>
          )}

          <ErrorBoundary name="bv-social-media-container">
            <DynamicComponent pageType="pip">
              <div className="sui-w-full sui-my-2 empty:sui-my-0">
                <BazaarVoiceCarousel
                  itemId={itemId}
                  siteName="homedepot-gqfoln"
                  lazyLoadGallery
                />
              </div>
            </DynamicComponent>
          </ErrorBoundary>

          <ErrorBoundary name="roomvo-inspire-banner">
            <DynamicComponent pageType="pip">
              <Hydrator
                id="product-section-roomvo-inspire-banner"
                className="grid"
                scrollBuffer={HYDRATION_STANDARD_SCROLL_BUFFER}
                placeholder={<Placeholder width="100%" height="150px" color="#F5F5F5" />}
              >
                <Col>
                  <RoomVoInspire />
                </Col>
              </Hydrator>
            </DynamicComponent>
          </ErrorBoundary>

          {!hideSponsoredPipBanner && (
            <SponsoredPipBanner
              browserId={thdCustomer?.mcvisID}
              pageContext={{
                label: 'pip',
                data: {
                  itemId,
                  treatment: 'A'
                },
              }}
              dynamic={{
                pageType: 'pip'
              }}
              hydrator={{
                id: 'hydrated-sponsored-pip-banner-A',
                placeholder: (
                  <Placeholder
                    color="#f5f5f5"
                    ready={false}
                    style={{ height: 75 }}
                    type="rect"
                  >
                    <div />
                  </Placeholder>
                )
              }}
            />
          )}

          {(!isConsumerApp) && (
            <Row tag={Row}>
              <ErrorBoundary name="text-an-expert">
                <TextAnExpert proUX={isB2BCustomer} useDynamicSMS={isB2BCustomer} />
              </ErrorBoundary>
            </Row>
          )}
          {!isB2BCustomer && (
            <ZoneCard tag={Row}>
              <ErrorBoundary name="what-we-offer">
                <WhatWeOffer itemId={itemId} />
              </ErrorBoundary>
            </ZoneCard>
          )}
          {!isB2BCustomer && (
            <ZoneCard tag={Row}>
              <ErrorBoundary name="delivery-and-installation">
                <DeliveryAndInstallation itemId={itemId} />
              </ErrorBoundary>
            </ZoneCard>
          )}

          {shouldDisplayReferrerRecsDefault && (
            <Row>
              <ErrorBoundary name="dynamic-recs">
                <DynamicRecsWrapper
                  schemaName="pip_alternatives"
                  anchorId={itemId}
                  errorBoundary
                  requestKey={RECS_REQUEST_KEY}
                  recsContext={productTaxonomy}
                  hydrator={{
                    className: 'grid',
                    tag: ZoneCard,
                    scrollBuffer: HYDRATION_STANDARD_SCROLL_BUFFER,
                    id: pipRecsPositionDefault ? 'RecsDefault' : 'pip_alternatives'
                  }}
                  impressionData={{
                    component: 'DynamicRecs',
                    type: 'product'
                  }}
                />
              </ErrorBoundary>
            </Row>
          )}
          {!hideSponsoredCarousel && (
            <RetailMediaCarousel
              breadCrumbs={productData?.product?.taxonomy?.breadCrumbs}
              pageContext={{
                schema: 'pip_sponsored',
                data: { itemId },
                isPip: true
              }}
              showSponsoredCarousel
              dynamic={{
                pageType: 'pip'
              }}
              hydrator={{
                className: 'grid',
                tag: ZoneCard,
                delay: 1500,
                scrollBuffer: HYDRATION_STANDARD_SCROLL_BUFFER
              }}
            />
          )}

          <ErrorBoundary name="feature-based-recommendations">
            <FeatureBasedRecommendationsImporter
              hideOnMobile
              itemId={itemId}
              qctx={qctx}
              errorBoundary
              hydrator={{
                tag: ZoneCard,
                className: 'grid'
              }}
              dynamic={{
                pageType: 'pip'
              }}
            />
          </ErrorBoundary>

          {shouldDisplayVisuallySimilar(productData) && (
            <Row>
              <DynamicRecsWrapper
                schemaName="visuallysimilar_1_0"
                anchorImage={productData?.product?.media?.image?.url}
                anchorId={itemId}
                errorBoundary
                checkMinimumProducts
                minimumProductCount={4}
                requestKey={RECS_REQUEST_KEY}
                recsContext={productTaxonomy}
                hydrator={{
                  className: 'grid',
                  tag: ZoneCard,
                  scrollBuffer: HYDRATION_STANDARD_SCROLL_BUFFER,
                  id: 'visuallysimilar_1_0'
                }}
                impressionData={{
                  component: 'DynamicRecs',
                  type: 'product'
                }}
              />
            </Row>
          )}
          {shouldDisplayIrgcollections(productData) && (
            <Row>
              <DynamicRecsWrapper
                schemaName="brand_based_collection"
                anchorId={itemId}
                isIRG
                isCategoryTab
                maxResults="18"
                errorBoundary
                requestKey={RECS_REQUEST_KEY}
                hideFavorites={false}
                hydrator={{
                  className: 'grid',
                  tag: ZoneCard,
                  scrollBuffer: HYDRATION_STANDARD_SCROLL_BUFFER,
                  id: 'recs_brand_based_collection'
                }}
                impressionData={{
                  component: 'DynamicRecs',
                  type: 'product'
                }}
              />
            </Row>
          )}
          {shouldDisplayIrgaccessories(productData) && !hideYouMightAlsoNeed && (
            <Row>
              <DynamicRecsWrapper
                schemaName="irgaccessories"
                anchorId={itemId}
                isIRG
                maxResults="18"
                errorBoundary
                requestKey={RECS_REQUEST_KEY}
                recsContext={productTaxonomy}
                hideFavorites={false}
                hydrator={{
                  className: 'grid',
                  tag: ZoneCard,
                  scrollBuffer: HYDRATION_STANDARD_SCROLL_BUFFER,
                  id: 'recs_irgaccessories'
                }}
                impressionData={{
                  component: 'DynamicRecs',
                  type: 'product'
                }}
              />
            </Row>
          )}
          <QueryProvider
            defaultVariables={defaultVariables?.current}
            persist
            cacheKey="rv-pip-recs"
          >
            <RecentlyViewedWrapper
              schemaName="rv_gm_pip_rr"
              errorBoundary
              hydrator={{
                className: 'grid',
                tag: ZoneCard,
                delay: 2000,
                scrollBuffer: 1500,
                id: 'rv_gm_pip_rr'
              }}
            />
          </QueryProvider>
          {!hideRelatedProducts && (
            <RelatedSearchImporter
              itemId={itemId}
              dynamic={{
                pageType: 'pip'
              }}
              hydrator={{
                className: 'col__12-12 sui-mb-4'
              }}
            />
          )}

          <PopularVariationsImporter
            productId={itemId}
            dynamic={{
              pageType: 'pip'
            }}
            hydrator={{
              className: 'col__12-12'
            }}
          />

          <DynamicComponent pageType="pip">
            <Hydrator
              id="product-section-seo"
              className="grid"
              delay={2000}
              placeholder={(
                <Placeholder
                  color="#f5f5f5"
                  ready={false}
                  style={{ height: 750 }}
                  type="rect"
                >
                  <div />
                </Placeholder>
              )}
              preserveCtxVal="clientStore"
              scrollBuffer={HYDRATION_STANDARD_SCROLL_BUFFER}
              waitFor={[EmtLinksLoad]}
            >
              <Col>
                <EmtLinks path={pathname} qctx={qctx} />
              </Col>
            </Hydrator>
          </DynamicComponent>
        </Row>
      </ErrorBoundary>
    </>
  );
};

ProductMobile.displayName = 'ProductPageMobile';

ProductMobile.themeProps = {
  hideConsumerCardMessaging: bool,
  hideSponsoredCarousel: bool,
  hideSponsoredPipBanner: bool,
  hideAddToList: bool,
  hideAisleBay: bool,
  hideYouMightAlsoNeed: bool,
  hideRelatedProducts: bool
};

ProductMobile.defaultThemeProps = {
  hideConsumerCardMessaging: false,
  hideSponsoredCarousel: false,
  hideSponsoredPipBanner: false,
  hideAddToList: false,
  hideAisleBay: false,
  hideYouMightAlsoNeed: false,
  hideRelatedProducts: false
};

ProductMobile.dataModel = extend(
  {
    product: params({ itemId: string().isRequired() }).shape({
      fulfillment: params({ storeId: string(), zipCode: string() }).shape({
        backordered: bool()
      })
    })
  },
  {
    clientOnlyProduct: params({
      itemId: string().isRequired,
      loyaltyMembershipInput: customType('LoyaltyMembershipInput').shape({
        svocID: string(),
        programTiers: arrayOf(shape({
          tier: string(),
          program: string()
        }))
      })
    }).shape({
      itemId: string(),
      dataSources: string(),
      bundleSpecificationDetails: shape({
        parents: arrayOf(shape({
          parentId: string()
        }))
      }),
      info: shape({
        recommendationFlags: shape({
          visualNavigation: bool(),
          ACC: bool(),
          collections: bool(),
        })
      }),
      availabilityType: shape({
        discontinued: bool(),
        type: string()
      }),
      fulfillment: params({ storeId: string() }).shape({
        backordered: bool(),
        fulfillmentOptions: arrayOf(shape({
          type: string(),
          fulfillable: bool(),
          services: arrayOf(shape({
            type: string(),
            locations: arrayOf(shape({
              isAnchor: bool(),
              inventory: shape({
                isLimitedQuantity: bool(),
                isOutOfStock: bool()
              })
            }))
          }))
        })),
      })
    })
  },
  {
    clientOnlyProduct: params({ itemId: string().isRequired }).shape({
      seoDescription: string()
    })
  },
  {
    products: params({ itemIds: arrayOf(string().isRequired()).isRequired() }).arrayOf({
      itemId: string(),
      fulfillment: params({ storeId: string() }).shape({
        anchorStoreStatusType: string(),
        backordered: bool(),
        backorderedShipDate: string(),
        bossExcludedShipStates: string(),
        bodfsAssemblyEligible: bool(),
        seasonStatusEligible: bool(),
        excludedShipStates: string(),
        fulfillmentOptions: shape({
          fulfillable: bool(),
          messages: string(),
          services: shape({
            type: string(),
            isDefault: bool(),
            deliveryCharge: string(),
            deliveryDates: shape({
              endDate: string(),
              startDate: string(),
            }),
            deliveryTimeline: string(),
            freeDeliveryThreshold: string(),
            totalCharge: string(),
            locations: shape({
              type: string(),
              isAnchor: bool(),
              locationId: string(),
              storeName: string(),
              storePhone: string(),
              state: string(),
              distance: string(),
              inventory: shape({
                quantity: string(),
                isInStock: bool(),
                isOutOfStock: bool(),
                isLimitedQuantity: bool(),
                isUnavailable: bool(),
                maxAllowedBopisQty: string(),
                minAllowedBopisQty: string(),
              }),
            })
          }),
          type: string()
        }),
      }),
    })
  },
  PIPRedirector,
  StickyNav,
  Breadcrumbs,
  Sharebelt,
  Discontinued,
  ProductInfoBar,
  MediaGalleryMobile,
  MediaGalleryCarousel,
  AisleBay,
  ProductDetails,
  ProductConfigurator,
  Promotion,
  PromotionProductsAnchor,
  SalientPoints,
  ProductPageEventCountdownTimer,
  Price,
  PriceClearanceDataModel,
  ConsumerCardMessaging,
  SuperSku,
  MajorApplianceBuyBoxModel,
  FeatureBasedRecommendationsImporter,
  EmtLinksModel,
  RelatedSearchImporter,
  PopularVariationsImporter,
  DeliveryAndInstallation,
  WhatWeOffer,
  DynamicRecsWrapper,
  PipSemWrapper,
  RecentlyViewedWrapper,
  ProductMetadata,
  SpecificationsAccordion,
  ProductOverviewAccordion,
  QuestionsAndAnswersAccordion,
  RatingsAndReviewsAccordion,
  PackagesMiniABTest,
  RoomVo
);
